// Fee & Obligation PR-4b — TanStack Query hooks for §17.4 time logging.
// Stable key factory + a mutation that invalidates the case's time-log view.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  workoutTimeApi,
  type LogWorkoutTimeRequest,
} from "@/api/portfolioAdmin/workoutTimeApi";
import { pr4bKeys } from "./usePr4b";
import { loanFeesKeys } from "./loanFeesKeys";

export const workoutTimeKeys = {
  activityCodes: () =>
    ["portfolio-admin", "workout-time", "activity-codes"] as const,
  timeLog: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "time-log"] as const,
};

export function useWorkoutTimeActivityCodes() {
  return useQuery({
    queryKey: workoutTimeKeys.activityCodes(),
    queryFn: () => workoutTimeApi.activityCodes(),
    staleTime: 60 * 60 * 1000, // the curated §17.4 list rarely changes
  });
}

export function useWorkoutTimeLog(caseId: string | undefined) {
  return useQuery({
    queryKey: workoutTimeKeys.timeLog(caseId ?? ""),
    queryFn: () => workoutTimeApi.getForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useLogWorkoutTime(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LogWorkoutTimeRequest) =>
      workoutTimeApi.log(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workoutTimeKeys.timeLog(caseId) });
    },
  });
}

// On-demand "bill unbilled time". Beyond the time-log view (entries now rolled
// up), the minted recoverable cost + pending recovery fee change the case
// "Costs vs recoveries" widget (pr4b costs) and the Fee & Obligation tab's Fees
// section + B3 fee-review queue — invalidate all of them.
export function useBillWorkoutTime(caseId: string, loanId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => workoutTimeApi.bill(caseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workoutTimeKeys.timeLog(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.costs(caseId) });
      qc.invalidateQueries({ queryKey: loanFeesKeys.reviewQueuePrefix() });
      qc.invalidateQueries({ queryKey: loanFeesKeys.crossLoanPrefix() });
      if (loanId) {
        qc.invalidateQueries({ queryKey: loanFeesKeys.loan(loanId) });
      }
    },
  });
}
